import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useStartDate } from '../StartDateContext';

// ---------------------------------------------------------------------------
// Helfer: Position aus Start-/Enddatum berechnen (wie in Task.js)
// ---------------------------------------------------------------------------
const calculateBarPosition = (barStartDate, barEndDate, visibleWeeks, weekWidth, startDate) => {
  if (!barStartDate || !barEndDate || !startDate) return { x: 0, width: 10 };

  const containerWidthPixels = visibleWeeks * weekWidth;
  const viewStartDate = new Date(startDate);
  const dayOfWeek = viewStartDate.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  viewStartDate.setDate(viewStartDate.getDate() + mondayOffset);
  viewStartDate.setHours(0, 0, 0, 0);

  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  const pixelsPerMs = weekWidth / msPerWeek;

  const startOffsetMs = barStartDate.getTime() - viewStartDate.getTime();
  const endOffsetMs = barEndDate.getTime() - viewStartDate.getTime();
  const startPx = startOffsetMs * pixelsPerMs;
  const endPx = endOffsetMs * pixelsPerMs;

  if (endPx <= 0 || startPx >= containerWidthPixels) {
    return { x: -100, width: 0 };
  }

  const visibleStartPx = Math.max(0, startPx);
  const visibleEndPx = Math.min(containerWidthPixels, endPx);
  const visibleWidthPx = visibleEndPx - visibleStartPx;

  const xPercent = parseFloat(((visibleStartPx / containerWidthPixels) * 100).toFixed(4));
  const widthPercent = parseFloat(((visibleWidthPx / containerWidthPixels) * 100).toFixed(4));

  return { x: xPercent, width: Math.max(widthPercent, 0.5) };
};

// ---------------------------------------------------------------------------
// Helfer: Sammle alle offenen Panels (eine Zeile pro offenem Panel)
// ---------------------------------------------------------------------------
const collectPanelRows = (groups, visibleWeeks, weekWidth, startDate) => {
  const rows = [];
  if (!groups || !Array.isArray(groups)) return rows;

  const collectFromPanels = (panels, groupTitle = '') => {
    if (!panels || !Array.isArray(panels)) return;
    panels.forEach(panel => {
      // Nur offene Panels berücksichtigen
      if (panel.open === false) return;

      if (panel.type === 'group') {
        // Rekursion in verschachtelte Gruppen
        collectFromPanels(panel.panels, panel.title || groupTitle);
      } else {
        // Einzelnes Panel = eine Zeile in der Minimap
        const tasks = (panel.movableBars || []).map(bar => {
          // Wenn Start-/Enddatum vorhanden sind, Position neu berechnen
          // (die gespeicherten x/width-Werte können bei Zoom/visibleWeeks-Änderung veraltet sein)
          const startDateObj = bar.startDate ? new Date(bar.startDate) : null;
          const endDateObj = bar.endDate ? new Date(bar.endDate) : null;
          let x = bar.x ?? 0;
          let width = bar.width ?? 10;
          if (startDateObj && endDateObj) {
            const pos = calculateBarPosition(startDateObj, endDateObj, visibleWeeks, weekWidth, startDate);
            if (pos.width > 0) {
              x = pos.x;
              width = pos.width;
            }
          }
          return {
            id: `${panel.id}_${bar.id}`,
            x,
            width,
            color: bar.backgroundColor || bar.color || '#2196f3',
            label: bar.label || ''
          };
        });
        rows.push({
          panelId: panel.id,
          panelTitle: panel.title || '',
          groupTitle: groupTitle,
          tasks
        });
      }
    });
  };

  groups.forEach(group => {
    if (group.open === false) return;
    collectFromPanels(group.panels, group.title || '');
  });

  return rows;
};

const Minimap = ({ groups, visibleWeeks, weekWidth, scrollContainerRef, contentRef }) => {
  const { startDate } = useStartDate();
  const [rows, setRows] = useState([]);
  const [viewport, setViewport] = useState({ x: 0, y: 0, width: 100, height: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const minimapRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const dragMappingRef = useRef(null);

  const MINIMAP_WIDTH = 300;
  const MINIMAP_HEIGHT = 180;
  const ROW_GAP = 2;          // px zwischen Zeilen
  const TOP_PADDING = 4;
  const BOTTOM_PADDING = 4;
  const MAX_ROW_HEIGHT = 28;  // Maximale Höhe pro Zeile (verhindert Verzerrung bei wenigen Panels)

  // -------------------------------------------------------------------------
  // Zeilen neu berechnen
  // -------------------------------------------------------------------------
  useEffect(() => {
    const newRows = collectPanelRows(groups, visibleWeeks, weekWidth, startDate);
    setRows(newRows);
  }, [groups, visibleWeeks, weekWidth, startDate]);

  // -------------------------------------------------------------------------
  // Zeilen-Layout: Einfache gleich große Zeilen
  // -------------------------------------------------------------------------
  const rowMetrics = useMemo(() => {
    const numRows = rows.length;
    if (numRows === 0) {
      return { rowHeight: MINIMAP_HEIGHT, rowAreaHeight: MINIMAP_HEIGHT };
    }
    const available = MINIMAP_HEIGHT - TOP_PADDING - BOTTOM_PADDING - (numRows - 1) * ROW_GAP;
    const h = Math.min(Math.max(available / numRows, 6), MAX_ROW_HEIGHT);
    const rowAreaH = numRows * h + (numRows - 1) * ROW_GAP;
    return { rowHeight: h, rowAreaHeight: rowAreaH };
  }, [rows]);

  const { rowHeight, rowAreaHeight } = rowMetrics;

  // Vertikale Position einer Zeile
  const rowTop = (idx) => TOP_PADDING + idx * (rowHeight + ROW_GAP);

  // Task innerhalb einer Zeile positionieren
  const taskTopInRow = (rowIdx, taskIndex, totalTasksInRow) => {
    if (totalTasksInRow <= 1) {
      return rowTop(rowIdx) + rowHeight / 2 - 1; // zentriert
    }
    const margin = rowHeight * 0.15;
    const available = rowHeight - 2 * margin;
    const step = available / Math.max(totalTasksInRow - 1, 1);
    return rowTop(rowIdx) + margin + taskIndex * step;
  };

  // -------------------------------------------------------------------------
  // DOM-basiertes Mapping: Content-Y ↔ Minimap-Y
  // -------------------------------------------------------------------------
  const buildMapping = useCallback(() => {
    const container = scrollContainerRef?.current;
    const content = contentRef?.current;
    if (!container || !content) return null;

    const firstChild = container.firstElementChild;
    const headerHeight = firstChild ? firstChild.getBoundingClientRect().height : 0;
    const contentVisibleH = Math.max(container.clientHeight - headerHeight, 1);

    const contentRect = content.getBoundingClientRect();
    const panelEls = content.querySelectorAll('[data-panel-id]');
    const panelPositions = [];
    panelEls.forEach(el => {
      if (el.dataset.panelOpen === 'false') return;
      const rect = el.getBoundingClientRect();
      // WICHTIG: rect.top - contentRect.top ist der KONSTANTE Offset des Panels
      // relativ zum Content-Top. Er ändert sich NICHT beim Scrollen.
      // scrollTop NICHT addieren!
      const y = rect.top - contentRect.top;
      panelPositions.push({ y, bottom: y + rect.height });
    });

    const numPanels = Math.min(panelPositions.length, rows.length);
    const rt = (idx) => TOP_PADDING + idx * (rowHeight + ROW_GAP);

    const contentYToMinimapY = (cY) => {
      if (numPanels === 0) return TOP_PADDING;
      if (cY <= panelPositions[0].y) return rt(0);
      const last = numPanels - 1;
      if (cY >= panelPositions[last].bottom) return rt(last) + rowHeight;
      for (let i = 0; i < last; i++) {
        const p = panelPositions[i];
        const next = panelPositions[i + 1];
        if (cY >= p.bottom && cY < next.y) {
          const frac = (cY - p.bottom) / Math.max(next.y - p.bottom, 1);
          return rt(i) + rowHeight + frac * (rt(i + 1) - rt(i) - rowHeight);
        }
        if (cY >= p.y && cY <= p.bottom) {
          const frac = (cY - p.y) / Math.max(p.bottom - p.y, 1);
          return rt(i) + frac * rowHeight;
        }
      }
      const p = panelPositions[last];
      if (cY >= p.y && cY <= p.bottom) {
        const frac = (cY - p.y) / Math.max(p.bottom - p.y, 1);
        return rt(last) + frac * rowHeight;
      }
      return rt(last) + rowHeight;
    };

    const minimapYToContentY = (mY) => {
      if (numPanels === 0) return 0;
      for (let i = 0; i <= numPanels - 1; i++) {
        const rowYTop = rt(i);
        const rowYBottom = rt(i) + rowHeight;
        if (mY >= rowYTop && mY <= rowYBottom) {
          const p = panelPositions[i];
          const frac = (mY - rowYTop) / rowHeight;
          return p.y + frac * (p.bottom - p.y);
        }
        if (i < numPanels - 1) {
          const nextRowTop = rt(i + 1);
          if (mY > rowYBottom && mY < nextRowTop) {
            const p = panelPositions[i];
            const next = panelPositions[i + 1];
            const frac = (mY - rowYBottom) / Math.max(nextRowTop - rowYBottom, 1);
            return p.bottom + frac * Math.max(next.y - p.bottom, 0);
          }
        }
      }
      if (mY < rt(0)) return panelPositions[0].y;
      return panelPositions[numPanels - 1].bottom;
    };

    return { headerHeight, contentVisibleH, contentScrollH: container.scrollHeight - headerHeight, contentYToMinimapY, minimapYToContentY };
  }, [scrollContainerRef, contentRef, rows, rowHeight]);

  // -------------------------------------------------------------------------
  // Viewport-Berechnung: Basierend auf tatsächlichen DOM-Panel-Positionen
  // -------------------------------------------------------------------------
  const updateViewport = useCallback(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const totalW = visibleWeeks * weekWidth;
    const scaleX = MINIMAP_WIDTH / totalW;

    const mapping = buildMapping();
    let vY, vH;

    if (!mapping) {
      vY = TOP_PADDING;
      vH = rowAreaHeight;
    } else {
      const visibleStart = container.scrollTop;
      const visibleEnd = visibleStart + mapping.contentVisibleH;
      const mapStart = mapping.contentYToMinimapY(visibleStart);
      const mapEnd = mapping.contentYToMinimapY(visibleEnd);
      vY = mapStart;
      vH = Math.max(mapEnd - mapStart, 4);
    }

    const vX = container.scrollLeft * scaleX;
    const vW = Math.max(container.clientWidth * scaleX, 4);

    setViewport({ x: vX, y: vY, width: vW, height: vH });
  }, [scrollContainerRef, visibleWeeks, weekWidth, rowAreaHeight, buildMapping]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    const content = contentRef?.current;
    if (!container || !content) return;

    updateViewport();

    const handleScroll = () => requestAnimationFrame(updateViewport);
    container.addEventListener('scroll', handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => requestAnimationFrame(updateViewport));
    resizeObserver.observe(container);
    resizeObserver.observe(content);

    const intervalId = setInterval(updateViewport, 800);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      clearInterval(intervalId);
    };
  }, [scrollContainerRef, contentRef, updateViewport]);

  // -------------------------------------------------------------------------
  // Klick → Scroll
  // -------------------------------------------------------------------------
  const handleMinimapClick = useCallback((e) => {
    if (isDragging) return;
    const container = scrollContainerRef?.current;
    const minimap = minimapRef.current;
    if (!container || !minimap) return;

    const rect = minimap.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const totalW = visibleWeeks * weekWidth;
    const scaleX = MINIMAP_WIDTH / totalW;

    const mapping = buildMapping();
    const targetScrollLeft = (clickX / scaleX) - (container.clientWidth / 2);

    let targetScrollTop;
    if (mapping) {
      const targetContentY = mapping.minimapYToContentY(clickY);
      targetScrollTop = targetContentY - (mapping.contentVisibleH / 2);
    } else {
      targetScrollTop = 0;
    }

    container.scrollTo({
      left: Math.max(0, targetScrollLeft),
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth'
    });
  }, [scrollContainerRef, visibleWeeks, weekWidth, isDragging, buildMapping]);

  // -------------------------------------------------------------------------
  // Drag → Live-Scroll
  // -------------------------------------------------------------------------
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    const container = scrollContainerRef?.current;
    const minimap = minimapRef.current;
    if (!container || !minimap) return;

    const rect = minimap.getBoundingClientRect();
    dragMappingRef.current = buildMapping();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      minimapY: e.clientY - rect.top,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop
    };
  }, [scrollContainerRef, buildMapping]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const container = scrollContainerRef?.current;
      if (!container) return;

      const totalW = visibleWeeks * weekWidth;
      const scaleX = MINIMAP_WIDTH / totalW;

      const mapping = dragMappingRef.current;
      if (!mapping) return;

      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;

      const startMinimapY = dragStartRef.current.minimapY;
      const newMinimapY = startMinimapY + dy;
      const startContentY = mapping.minimapYToContentY(startMinimapY);
      const newContentY = mapping.minimapYToContentY(newMinimapY);

      container.scrollLeft = dragStartRef.current.scrollLeft + (dx / scaleX);
      container.scrollTop = dragStartRef.current.scrollTop + (newContentY - startContentY);
    };

    const handleMouseUp = () => {
      dragMappingRef.current = null;
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, scrollContainerRef, visibleWeeks, weekWidth]);

  // -------------------------------------------------------------------------
  // Wochen-Spalten
  // -------------------------------------------------------------------------
  const weekColumns = useMemo(() => {
    const cols = [];
    for (let i = 0; i < visibleWeeks; i++) {
      cols.push({
        index: i,
        left: (i / visibleWeeks) * 100,
        width: (1 / visibleWeeks) * 100,
        color: i % 2 === 0 ? 'rgba(187,222,251,0.25)' : 'rgba(241,245,254,0.25)'
      });
    }
    return cols;
  }, [visibleWeeks]);

  return (
    <div
      ref={minimapRef}
      onClick={handleMinimapClick}
      onMouseDown={handleMouseDown}
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: MINIMAP_WIDTH,
        height: MINIMAP_HEIGHT,
        background: 'rgba(248,249,250,0.97)',
        borderRadius: 8,
        border: '1px solid rgba(255,255,255,0.6)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8)',
        zIndex: 50,
        overflow: 'hidden',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none'
      }}
      title="Minimap: Klicken oder Ziehen zum Navigieren"
    >
      {/* Wochen-Hintergrund */}
      {weekColumns.map(week => (
        <div
          key={week.index}
          style={{
            position: 'absolute',
            left: `${week.left}%`,
            top: 0,
            width: `${week.width}%`,
            height: '100%',
            backgroundColor: week.color,
            borderRight: '1px solid rgba(204,204,204,0.15)'
          }}
        />
      ))}

      {/* Zeilen-Hintergrund (eine Zeile pro offenem Panel) */}
      {rows.map((row, idx) => (
        <div
          key={`row-${row.panelId}-${idx}`}
          style={{
            position: 'absolute',
            left: 0,
            top: rowTop(idx),
            width: '100%',
            height: rowHeight,
            backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.08)',
            borderBottom: '1px solid rgba(0,0,0,0.10)'
          }}
          title={row.panelTitle || `Panel ${idx + 1}`}
        />
      ))}

      {/* Task-Rechtecke */}
      {rows.map((row, rowIdx) =>
        row.tasks.map((task, taskIdx) => {
          const tTop = taskTopInRow(rowIdx, taskIdx, row.tasks.length);
          return (
            <div
              key={task.id}
              style={{
                position: 'absolute',
                left: `${task.x}%`,
                top: tTop,
                width: `${Math.max(task.width, 0.3)}%`,
                height: 2,
                backgroundColor: task.color,
                borderRadius: 1,
                opacity: 0.9,
                zIndex: 2
              }}
              title={`${row.panelTitle}${task.label ? ': ' + task.label : ''}`}
            />
          );
        })
      )}

      {/* Viewport-Overlay */}
      <div
        style={{
          position: 'absolute',
          left: viewport.x,
          top: viewport.y,
          width: viewport.width,
          height: viewport.height,
          backgroundColor: 'rgba(255,255,255,0.30)',
          border: '1px solid rgba(0,0,0,0.35)',
          borderRadius: 3,
          boxShadow: 'inset 0 0 4px rgba(0,0,0,0.1)',
          pointerEvents: 'none',
          zIndex: 3
        }}
      />

      {/* 3D-Rahmen-Overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 8,
          border: '1px solid rgba(0,0,0,0.08)',
          pointerEvents: 'none',
          boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.9), inset 0 -1px 2px rgba(0,0,0,0.05)',
          zIndex: 4
        }}
      />
    </div>
  );
};

export default Minimap;
